import axios, {
    AxiosHeaders,
    AxiosRequestConfig,
    RawAxiosRequestHeaders,
} from 'axios'
import { ResStatus, ResWrapper } from './ResWrapper'
import { getTokenOrRedirect } from './auth0Helper'
import { AuthHelper } from '../auth/AuthHelper'

class HttpWrapper {
    private static instance: HttpWrapper

    public constructor() {
        if (HttpWrapper.instance) {
            return HttpWrapper.instance
        }

        HttpWrapper.instance = this
    }

    public async get<TRes>(
        relativeUrl: string,
        queryParams?: URLSearchParams,
        config?: AxiosRequestConfig,
        authorised: boolean = true,
    ): Promise<TRes> {
        const fullUrl = new URL(
            relativeUrl,
            import.meta.env.VITE_BACKEND_BASE_URL,
        )

        queryParams = this.appendTenantOverrideId(queryParams)

        if (queryParams) {
            fullUrl.search = queryParams.toString()
        }

        const authConfig = {
            ...config,
            headers: authorised
                ? await this.appendAuthHeaders(config?.headers)
                : config?.headers,
        }

        const response = await axios.get(fullUrl.toString(), authConfig)

        // Bypass JSON processing if responseType is blob
        if (config?.responseType === 'blob') {
            return response.data as TRes
        }

        return this.handleRes<TRes>(response.data)
    }

    public async post<TReq, TRes>(
        relativeUrl: string,
        data?: TReq,
        queryParams?: URLSearchParams,
        config?: AxiosRequestConfig,
        authorised: boolean = true,
    ): Promise<TRes> {
        const fullUrl = new URL(
            relativeUrl,
            import.meta.env.VITE_BACKEND_BASE_URL,
        )

        // if (queryParams) {
        //     queryParams.append('apiKey', this.getApiKey())
        // } else {
        //     queryParams = new URLSearchParams({ apiKey: this.getApiKey() })
        // }

        queryParams = this.appendTenantOverrideId(queryParams)

        if (queryParams) {
            fullUrl.search = queryParams.toString()
        }

        const response = await axios.post(fullUrl.toString(), data, {
            ...config,
            headers: authorised
                ? await this.appendAuthHeaders(config?.headers)
                : config?.headers,
        })

        return this.handleRes<TRes>(response.data)
    }

    private appendTenantOverrideId(
        queryParams?: URLSearchParams,
    ): URLSearchParams | undefined {
        const tenantOverrideId = AuthHelper.getTenantOverrideId()

        if (tenantOverrideId) {
            if (queryParams) {
                queryParams.append('tenantOverrideId', tenantOverrideId)
            } else {
                queryParams = new URLSearchParams({ tenantOverrideId })
            }
        }

        return queryParams
    }

    private async appendAuthHeaders(
        existingHeaders?: RawAxiosRequestHeaders | AxiosHeaders,
    ): Promise<Record<string, string>> {
        // Normalize existing headers
        const headers: Record<string, string> = {}

        // If existingHeaders is provided, convert its values to strings
        if (existingHeaders && typeof existingHeaders === 'object') {
            Object.entries(existingHeaders).forEach(([key, value]) => {
                if (value !== undefined) {
                    headers[key] = String(value)
                }
            })
        }

        // Add Authorization header
        const bearerToken = await getTokenOrRedirect()
        headers['Authorization'] = `Bearer ${bearerToken}`

        return headers
    }

    private handleRes<TRes>(resData: ResWrapper<TRes>): TRes {
        // if we're in here that means we got a 2xx response - but did the server respond with an error?

        if (resData.status === ResStatus.ERROR) {
            // generic error - we can't handle this (as opposed to a token refresh required etc)
            throw new Error(
                `There was an issue communicating with the Contxt server: ${resData.errorMsg}`,
            )
        } else {
            return resData.data!
        }
    }
}

const httpWrapper = new HttpWrapper()
export default httpWrapper
